import {UseQueryResult} from '@tanstack/react-query';
import {Dayjs} from 'dayjs';
import {z as r} from 'zod';

import {PostsFilterQueriesType} from '../../page/main/posts/posts-filter/posts-filter-type';
import {shortCatalogSchema, ShortCatalogType} from '../../provider/catalogs/catalogs-type';
import {CursorPaginationHookType} from '../../util/cursor-pagination/cursor-pagination-type';
import {PaginationDataType, PaginationType} from '../../util/pagination-hook/pagination-hook-type';
import {TimeRangeType} from '../../util/time';
import {ProvidersEnum} from '../../util/type';
import {cursorPaginationResponseSchemaFactory, generateResponseSchema} from '../api/api-type';
import {UseHookType} from '../api-hook/api-hook-type';

export enum PostStatusEnum {
    Draft = 'draft',
    Deleted = 'deleted',
    Planned = 'planned',
    Sent = 'sent',
    Active = 'active',
    Sending = 'in_progress',
}

export enum CalendarPostStatusEnum {
    publishPlanned = 'publish_planned',
    deletePlanned = 'deleting_planned',
    published = 'published',
    refreshPlanned = 'refresh_planned',
}

export enum PostActionEnum {
    PUBLISH = 'publish',
    REMOVE = 'delete',
    EDIT = 'edit',
}

type PostsHookAdditionalType = {
    cursorPagination: CursorPaginationHookType;
    refresh: () => void;
    count?: number | null;
};

export type PostsHookPropsType = {
    filter: PostsFilterQueriesType;
    mainFilterKey: string;
    enabled?: boolean;
};

export enum PostFormFieldsEnum {
    Brands = 'brandsIds',
    Timezone = 'timezone',
    CreateDateTime = 'datetimeCreation',
    DeleteDateTime = 'datetimeDeleting',
    Status = 'status',
    Name = 'name',
    Posts = 'posts',
    SelectorId = 'selectorId',
    VkGroupsIds = 'vkGroupsIds',
    FbPagesIds = 'fbPagesIds',
    InstagramGroupsIds = 'instagramGroupsIds',
    Id = 'id',
    AutoRefreshFrequency = 'autorefreshFrequency',
    AutoRefreshCount = 'autorefreshCount',
}

const postListItemScheme = r.object({
    id: r.number(),
    name: r.string(),
    status: r.nativeEnum(PostStatusEnum),
    createdAt: r.string(),
    catalogs: r.array(
        shortCatalogSchema.merge(
            r.object({
                isHasPost: r.boolean(),
            })
        )
    ),
    canBeRefreshed: r.boolean(),
    [PostFormFieldsEnum.AutoRefreshCount]: r.number().optional().nullable(),
    [PostFormFieldsEnum.AutoRefreshFrequency]: r.number().optional().nullable(),
});

export const postsListResponseSchema = cursorPaginationResponseSchemaFactory(postListItemScheme);

export type PostListItemType = r.infer<typeof postListItemScheme>;

export type PostsListResponseType = r.infer<typeof postsListResponseSchema>;

export type PostsHookType = UseHookType<Array<PostListItemType>> & PostsHookAdditionalType;

export enum PostsFilterKeyEnum {
    sources = 'sources',
    statuses = 'statuses',
    timeRange = 'timeRange',
}

export type PostsFilterStateType = {
    [PostsFilterKeyEnum.sources]: Array<number>;
    [PostsFilterKeyEnum.statuses]: Array<PostStatusEnum>;
    [PostsFilterKeyEnum.timeRange]: TimeRangeType;
};

export type PostsDrawerFilterType = Readonly<PostsFilterStateType>;

export type SetPostFilterType = (filterPartial: Partial<PostsFilterStateType>) => void;

export type UsePostFilterHookType = {
    filter: PostsFilterStateType;
    setFilter: SetPostFilterType;
};

export enum PostTypeEnum {
    standard = 'standard',
    googleEvent = 'event',
    googleOffer = 'offer',
}
export enum PostSourceEnum {
    google = 'google',
    yandex = 'yandex',
    facebook = 'facebook',
    vkontakte = 'vk',
    instagram = 'instagram',
}

export enum GoogleButtonTypeEnum {
    Unspecified = 'unspecified',
    Book = 'book',
    Order = 'order',
    Shop = 'shop',
    LearnMore = 'learn_more',
    SignUp = 'sign_up',
    Call = 'call',
}

const calendarPostsRecordSchema = r
    .record(
        r.nativeEnum(CalendarPostStatusEnum),
        r.array(
            r.object({
                id: r.number(),
                name: r.string(),
            })
        )
    )
    .optional();

const calendarPostsSchema = r.record(r.number(), calendarPostsRecordSchema);

export type CalendarPostsResponseType = r.infer<typeof calendarPostsSchema>;

export type CalendarPostsRequestParametersType = {
    year: number;
    month: number | null;
};

export type PostsAvailableCatalogsHookType = {
    availableCatalogs: Array<ShortCatalogType>;
    isInProgress: boolean;
    processError: Error | null;
    catalogsInfo: {
        [key in PostSourceEnum]: ShortCatalogType | null;
    };
};

export const lastPostsSchema = r.object({
    id: r.number(),
    name: r.string(),
    createdAt: r.string(),
    catalogsCount: r.number(),
    status: r.nativeEnum(PostStatusEnum),
});

export type LastPostsType = r.infer<typeof lastPostsSchema>;

type LastPostsAdditionalType = {
    getLastPosts: () => void;
};

export type LastPostsHookType = UseHookType<Array<LastPostsType>> & LastPostsAdditionalType;

export const postVkGroupSchema = r.object({
    groupId: r.number(),
    name: r.string(),
    screenName: r.string(),
    brand: r.string(),
});

export type PostVkGroupType = r.infer<typeof postVkGroupSchema>;

export enum CatalogPostStatusEnum {
    Created = 'created',
    Updated = 'updated',
    Deleted = 'deleted',
    Error = 'error',
    OnModeration = 'on_moderation',
    Rejected = 'rejected',
}

const postLinkItemSchema = r.object({
    id: r.number(),
    catalog: r.nativeEnum(ProvidersEnum),
    currentStatus: r.nativeEnum(CatalogPostStatusEnum),
    refreshing: r.boolean().optional().default(false),
    address: r.string().nullable(),
    pageLink: r.string().nullable(),
    sourceType: r.literal('web_page').or(r.literal('company')),
    sourceName: r.string(),
    catalogLink: r.string().nullable(),
    companyCode: r.string().optional(),
});

export const postLinksResponseSchema = generateResponseSchema(postLinkItemSchema).merge(
    r.object({
        commonPostName: r.string(),
    })
);

export type PostLinkType = r.infer<typeof postLinkItemSchema>;

export type PostLinksFilterType = {
    statuses: Array<CatalogPostStatusEnum>;
    providers: Array<PostSourceEnum>;
};

export type PostCatalogLinksResultType = r.infer<typeof postLinksResponseSchema>;

export type CatalogLinksHookType = UseQueryResult<PostCatalogLinksResultType> &
    PaginationType & {
        setFilter: (newValue: PostLinksFilterType) => void;
    };

export type PostLinkRequestOptionType = PaginationDataType & {
    postId: number;
} & PostLinksFilterType;

const postSubmitInformationCatalogStatsSchema = r.object({
    catalog: r.nativeEnum(ProvidersEnum),
    locationsCount: r.number(),
});

export const postSubmitInformationSchema = r.object({
    total: r.number(),
    catalogStats: r.array(postSubmitInformationCatalogStatsSchema),
});

export type PostSubmitInformationType = r.infer<typeof postSubmitInformationSchema>;
export type PostSubmitInformationParametersType = {
    selectorId: string;
    providerCodes: Array<ProvidersEnum>;
};
export const postInstagramPageSchema = r.object({
    groupId: r.string(),
    igId: r.string(),
    name: r.string(),
});

export type PostInstagramPageType = r.infer<typeof postInstagramPageSchema>;

export enum AiPostTypeEnum {
    promo = 'promo',
    companyNews = 'company_news',
    eventAnnouncement = 'event_announcement',
    informational = 'informational',
    greeting = 'greeting',
}

export enum AiImageSizeEnum {
    square = '1024x1024',
    portrait = '1024x1536',
    landscape = '1536x1024',
}

export enum AiGenerationErrorEnum {
    serviceUnavailable = 'SERVICE_UNAVAILABLE',
    moderationRejected = 'MODERATION_REJECTED',
    internal = 'INTERNAL',
}

const postAiSuggestCtaSchema = r.object({
    text: r.string(),
    url: r.string().nullable(),
});

const postAiSuggestItemSchema = r.object({
    id: r.string().optional(),
    text: r.string(),
    cta: postAiSuggestCtaSchema.nullable().optional(),
    perCatalog: r.record(r.string(), r.string()).nullable().optional(),
});

export type PostAiSuggestItemType = r.infer<typeof postAiSuggestItemSchema>;

// Centrifugo "text" event: `text` is a RAW JSON string the frontend must parse into {text, cta}.
export const postAiSuggestTextActionSchema = r.object({
    requestId: r.string(),
    type: r.literal('text'),
    text: r.string(),
    stop: r.boolean().optional(),
    error: r.string().nullable().optional(),
});

export type PostAiSuggestTextActionType = r.infer<typeof postAiSuggestTextActionSchema>;

// Legacy Centrifugo "suggestions" event (ai_post_generation_v2 OFF): {requestId, postSuggestions: string[]}.
// Has no `type` field, which distinguishes it from the v2 text/image events.
// Remove together with the rollout flag after full v2 rollout.
export const postAiSuggestLegacyActionSchema = r.object({
    requestId: r.string(),
    postSuggestions: r.array(r.string()),
});

export type PostAiSuggestLegacyActionType = r.infer<typeof postAiSuggestLegacyActionSchema>;

// Shape encoded inside the `text` raw JSON string of a text event.
export const postAiSuggestTextPayloadSchema = r.object({
    text: r.string(),
    cta: postAiSuggestCtaSchema.nullable().optional(),
});

// Centrifugo "image" event item: backend publishes only {url, size}.
const postAiGeneratedImageSchema = r.object({
    url: r.string(),
    size: r.string(),
});

export type PostAiGeneratedImageType = r.infer<typeof postAiGeneratedImageSchema>;

export const postAiSuggestImageActionSchema = r.object({
    requestId: r.string(),
    type: r.literal('image'),
    images: r.array(postAiGeneratedImageSchema).optional(),
    error: r.string().nullable().optional(),
});

export type PostAiSuggestImageActionType = r.infer<typeof postAiSuggestImageActionSchema>;

const postGenerationOptionItemSchema = r.object({
    key: r.string(),
    label: r.string(),
});

export const postGenerationOptionsSchema = r.object({
    postTypes: r.array(postGenerationOptionItemSchema),
    imageSizes: r.array(postGenerationOptionItemSchema),
    imageEnabled: r.boolean(),
});

export type PostGenerationOptionsType = r.infer<typeof postGenerationOptionsSchema>;

// Response of the reused post-image save endpoint (/v1/posts/images/): permanent image URL.
export const saveGeneratedImageResponseSchema = r.object({
    image: r.string(),
});

export type SaveGeneratedImageResponseType = r.infer<typeof saveGeneratedImageResponseSchema>;

export type PostAiGenerationSettingsType = {
    postType: AiPostTypeEnum;
    userPrompt?: string;
    choicesCount: number;
    generateImage: boolean;
    imageSize?: AiImageSizeEnum;
    imageCount: number;
    catalogs: Array<string>;
};

export type PostAiSuggestApplyType = {
    text: string;
    image: PostAiGeneratedImageType | null;
    cta: r.infer<typeof postAiSuggestCtaSchema> | null;
};

export type PostAiSuggestHookType = {
    // suggests[i] (text) + images[i] (photo, may be null) form slide i.
    suggests: Array<PostAiSuggestItemType>;
    images: Array<PostAiGeneratedImageType | null>;
    activeSuggestIndex: number;
    setActiveSuggestIndex: (index: number) => void;
    isGeneratingSuggest: boolean;
    isGeneratingImage: boolean;
    generatingImageIndex: number | null;
    requestSuggest: (settings: Omit<PostAiGenerationSettingsType, 'catalogs'>) => void;
    requestMoreSuggests: () => void; // adds a brand-new slide (text + image), FIFO max 3
    regenerateImage: (index: number) => void;
    deleteImage: (index: number) => void;
    editText: (index: number, text: string) => void;
    lastSettings: PostAiGenerationSettingsType | null;
};

export enum SourcePostFormFieldsEnum {
    Text = 'text',
    TopicType = 'topicType',
    ButtonLink = 'buttonLink',
    ButtonType = 'actionType',
    Catalog = 'catalog',
    Images = 'images',
    Offer = 'offer',
    Event = 'event',
    Video = 'video',
}

export const enum PostFormModeEnum {
    View = 'view',
    New = 'new',
    EditDraft = 'editDraft',
    EditExisted = 'editExisted',
    Copy = 'copy',
}

const uploadImageResponseSchema = r.object({
    image: r.string(),
    id: r.string(),
});

export type UploadImageResponseType = r.infer<typeof uploadImageResponseSchema>;

export type PostImageType = {
    isMain: boolean;
    url: string;
};

export const postVideoSchema = r.object({
    id: r.number(),
    url: r.string(),
    duration: r.number(),
    size: r.number(),
    name: r.string(),
    previewImageUrl: r.string(),
});

export type PostVideoType = r.infer<typeof postVideoSchema>;

const googleOfferSchema = r.object({
    couponCode: r.string().optional(),
    redeemOnlineUrl: r.string().optional(),
    termsConditions: r.string().optional(),
});

export type GoogleEventType = {
    title: string;
    start: Dayjs;
    end: Dayjs;
};

const fetchSourcePostSchema = r.object({
    [SourcePostFormFieldsEnum.Text]: r.string(),
    [SourcePostFormFieldsEnum.TopicType]: r.nativeEnum(PostTypeEnum).optional(),
    [SourcePostFormFieldsEnum.ButtonLink]: r.string().nullable().optional(),
    [SourcePostFormFieldsEnum.ButtonType]: r.nativeEnum(GoogleButtonTypeEnum).optional(),
    [SourcePostFormFieldsEnum.Catalog]: r.nativeEnum(PostSourceEnum),
    [SourcePostFormFieldsEnum.Images]: r.array(
        r.object({
            url: r.string(),
            isMain: r.boolean(),
        })
    ),
    [SourcePostFormFieldsEnum.Offer]: googleOfferSchema.optional().nullable(),
    [SourcePostFormFieldsEnum.Event]: r
        .object({
            title: r.string(),
            start: r.string(),
            end: r.string(),
        })
        .optional()
        .nullable(),
    [SourcePostFormFieldsEnum.Video]: postVideoSchema.nullable().optional(),
});

export type FetchSourcePostType = r.infer<typeof fetchSourcePostSchema>;

export const fetchPostSchema = r.object({
    [PostFormFieldsEnum.Id]: r.number().optional(),
    [PostFormFieldsEnum.Timezone]: r.string().nullable(),
    [PostFormFieldsEnum.Brands]: r.array(r.number()).nullable().optional(),
    [PostFormFieldsEnum.CreateDateTime]: r.string().nullish(),
    [PostFormFieldsEnum.DeleteDateTime]: r.string().nullish(),
    [PostFormFieldsEnum.Status]: r.nativeEnum(PostStatusEnum),
    [PostFormFieldsEnum.Name]: r.string(),
    [PostFormFieldsEnum.Posts]: r.array(fetchSourcePostSchema),
    [PostFormFieldsEnum.VkGroupsIds]: r.array(r.string()).nullable(),
    [PostFormFieldsEnum.FbPagesIds]: r.array(r.string()).nullable(),
    [PostFormFieldsEnum.InstagramGroupsIds]: r.array(r.string()).nullable(),
    [PostFormFieldsEnum.SelectorId]: r.string(),
    [PostFormFieldsEnum.AutoRefreshCount]: r.number().nullable(),
    [PostFormFieldsEnum.AutoRefreshFrequency]: r.number().nullable(),
    nextRefreshDatetime: r.string().nullish(),
    companiesCount: r.number().nullish(),
});

export type FetchPostType = r.infer<typeof fetchPostSchema>;

export type SourcePostType = Omit<FetchSourcePostType, SourcePostFormFieldsEnum.Event> & {
    [SourcePostFormFieldsEnum.Event]?: GoogleEventType;
};

export type PostFormType = {
    [PostFormFieldsEnum.Timezone]: string | null;
    [PostFormFieldsEnum.Brands]: Array<number>;
    [PostFormFieldsEnum.Status]: PostStatusEnum;
    [PostFormFieldsEnum.Name]: string;
    [PostFormFieldsEnum.Posts]: Array<SourcePostType>;
    [PostFormFieldsEnum.VkGroupsIds]: Array<string>;
    [PostFormFieldsEnum.FbPagesIds]: Array<string>;
    [PostFormFieldsEnum.InstagramGroupsIds]: Array<string>;
    [PostFormFieldsEnum.SelectorId]: string;
    [PostFormFieldsEnum.CreateDateTime]: Dayjs | null;
    [PostFormFieldsEnum.DeleteDateTime]: Dayjs | null;
    [PostFormFieldsEnum.Id]?: number;
    [PostFormFieldsEnum.AutoRefreshFrequency]: number | null;
    [PostFormFieldsEnum.AutoRefreshCount]: number | null;
};

export const postPreviewDetailsSchema = r.object({
    name: r.string().nullable(),
    logo: r.string().nullable(),
});

export type PostPreviewOptionsType = {
    selectorId?: string;
    groupIds?: Array<string>;
    igGroupsIds?: Array<string>;
    fbGroupsIds?: Array<string>;
};

export type PostPreviewDetailsType = r.infer<typeof postPreviewDetailsSchema>;

export enum PostImageUploaderErrorEnum {
    UnsupportedImageFormat = 'Unsupported file type. Allowed types are: image/jpg, image/jpeg, image/png',
}
